import { Link } from 'react-router-dom';
import { NotEntitledError, TierLockedError, type PlanTier } from '../lib/api-errors';

const TIER_LABELS: Readonly<Record<PlanTier, string>> = {
  explorer: 'Explorer',
  professional: 'Professional',
  team: 'Team',
  enterprise: 'Enterprise',
};

interface ApiErrorNoticeProps {
  readonly error: unknown;
  readonly fallbackMessage: string;
}

/**
 * Renders an API failure. A plan denial (TierLockedError) gets an upgrade prompt
 * linking to Billing; an entitlement denial (NotEntitledError) explains that the
 * account needs review, without an upgrade CTA; anything else keeps the standard
 * error box. Messages render as React text (escaped).
 */
export function ApiErrorNotice({ error, fallbackMessage }: ApiErrorNoticeProps): React.ReactElement {
  if (error instanceof TierLockedError) {
    const tierLabel = error.requiredTier ? TIER_LABELS[error.requiredTier] : null;
    return (
      <div
        className="rounded-md border border-sentinel-500/30 bg-sentinel-50 p-5 text-sm text-slate-700"
        role="status"
        data-testid="tier-locked-notice"
      >
        <p className="font-semibold text-slate-800">
          {tierLabel ? `Available on the ${tierLabel} plan` : 'Not included in your current plan'}
        </p>
        <p className="mt-1">{error.message}</p>
        <Link
          to="/billing"
          className="mt-3 inline-block rounded-md bg-sentinel-700 px-4 py-2 text-sm font-medium text-white hover:bg-sentinel-900"
        >
          {tierLabel ? `Upgrade to ${tierLabel}` : 'View plans'}
        </Link>
      </div>
    );
  }

  if (error instanceof NotEntitledError) {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 p-5 text-sm text-slate-700"
        role="status"
        data-testid="not-entitled-notice"
      >
        <p className="font-semibold text-slate-800">Not enabled for your account</p>
        <p className="mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
      {error instanceof Error ? error.message : fallbackMessage}
    </div>
  );
}
